/** Calendar view state (URL) and week maths. Weeks are Budapest weeks (Mon–Sun); quotas count Budapest days. */
import type { Schemas } from "../../api/client";
import { addDays, budapestDay, budapestTime, budapestToUnix, isDay, isTime, mondayOf, todayBudapest } from "../lib/time";
import { intParam, type Patch } from "../lib/url";

type Post = Schemas["PostOut"];
type Account = Schemas["AccountOut"];
type QuotaDay = Schemas["QuotaDay"];

export const ACCOUNT_IDS = ["a1", "a2", "a3", "a4"] as const;

export type CalParams = {
  week: string; // Monday, YYYY-MM-DD
  render?: number; // selected approved render to place
  account?: string;
  day?: string;
  time?: string;
  move?: number; // post being moved
  posted?: number; // post being marked posted
  post?: number; // post to show (links from notifications and jobs): jump to its week, highlight it
};

export function parseCalParams(sp: URLSearchParams, today: string = todayBudapest()): CalParams {
  const w = sp.get("week");
  const day = sp.get("day");
  const time = sp.get("time");
  const account = sp.get("account");
  return {
    week: mondayOf(isDay(w) ? w : today),
    render: intParam(sp.get("render")),
    account: account && (ACCOUNT_IDS as readonly string[]).includes(account) ? account : undefined,
    day: isDay(day) ? day : undefined,
    time: isTime(time) ? time : undefined,
    move: intParam(sp.get("move")),
    posted: intParam(sp.get("posted")),
    post: intParam(sp.get("post")),
  };
}

export function calParamsToPatch(p: CalParams): Patch {
  return { week: p.week, render: p.render, account: p.account, day: p.day, time: p.time, move: p.move, posted: p.posted, post: p.post };
}

/** The Budapest week (its Monday) a post falls in. */
export function postWeek(tPost: number): string {
  return mondayOf(budapestDay(tPost));
}

export function weekRange(monday: string): { from: number; to: number } {
  return { from: budapestToUnix(monday), to: budapestToUnix(addDays(monday, 7)) };
}

export function weekLabel(monday: string): string {
  return `${monday} – ${addDays(monday, 6)}`;
}

export function cellKey(account: string, day: string): string {
  return `${account}|${day}`;
}

/** Posts grouped by (account, Budapest day), each list sorted by time. */
export function postsByCell(posts: Post[]): Map<string, Post[]> {
  const m = new Map<string, Post[]>();
  for (const p of [...posts].sort((a, b) => a.t_post - b.t_post)) {
    const k = cellKey(p.account_id, budapestDay(p.t_post));
    m.set(k, [...(m.get(k) ?? []), p]);
  }
  return m;
}

export function quotaFor(quota: QuotaDay[], accounts: Account[], cellPosts: Post[], account: string, day: string): { used: number; quota: number } {
  const q = quota.find((x) => x.account_id === account && x.day === day);
  if (q) return { used: q.used, quota: q.quota };
  const acc = accounts.find((a) => a.id === account);
  return { used: cellPosts.filter((p) => p.status !== "skipped" && p.status !== "failed").length, quota: acc?.quota_per_day ?? 2 };
}

export function lanes(accounts: Account[]): string[] {
  const ids = accounts.map((a) => a.id);
  return ids.length ? ids : [...ACCOUNT_IDS];
}

export function slotUnix(day: string, time: string): number {
  return budapestToUnix(day, time);
}

export function postSlot(p: Post): { account: string; day: string; time: string } {
  return { account: p.account_id, day: budapestDay(p.t_post), time: budapestTime(p.t_post) };
}

